import { useState } from "react";
import { toast } from "sonner";

interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

interface PendingChanges {
  changes: FieldChange[];
  onSave: (reason: string) => Promise<void>;
}

export function useSaveWithAudit() {
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges | null>(null);

  const detectChanges = (oldData: any, newData: any): FieldChange[] => {
    const changes: FieldChange[] = [];
    
    for (const [field, newValue] of Object.entries(newData)) {
      const oldValue = oldData[field];
      
      // Handle array comparisons
      if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({ field, oldValue, newValue });
        }
      } else if (oldValue !== newValue) {
        changes.push({ field, oldValue, newValue });
      }
    }
    
    return changes;
  };

  const requestSave = (
    oldData: any,
    newData: any,
    onSave: (reason: string) => Promise<void>
  ) => {
    const changes = detectChanges(oldData, newData);

    if (changes.length === 0) {
      toast.info("No changes detected");
      return false;
    }

    setPendingChanges({ changes, onSave });
    setShowReasonDialog(true);
    return true;
  };

  const confirmSave = async (reason: string) => {
    if (!pendingChanges) return;

    try {
      await pendingChanges.onSave(reason);
      setShowReasonDialog(false);
      setPendingChanges(null);
    } catch (error) {
      console.error("Save failed:", error);
      throw error;
    }
  };

  const cancelSave = () => {
    setShowReasonDialog(false);
    setPendingChanges(null);
  };

  return {
    showReasonDialog,
    pendingChanges,
    requestSave,
    confirmSave,
    cancelSave,
  };
}
